import { config } from './config';

interface FormOptions {
  formSelector: string;
  pageName?: string;
  serviceType?: string;
  customFields?: Record<string, string>;
  serviceMapping?: Record<string, string>;
}

/**
 * Format form data into a WhatsApp message
 */
export function formatWhatsAppMessage(formData: Record<string, string>, options: {
  pageName: string;
  serviceType?: string;
  serviceMapping?: Record<string, string>;
}): string {
  const { pageName, serviceType, serviceMapping } = options;

  let message = `🙏 *New Consultation Request*\n\n`;
  message += `📋 *Contact Details*\n`;
  message += `• Name: ${formData.name || 'Not provided'}\n`;
  message += `• Email: ${formData.email || 'Not provided'}\n`;
  message += `• Phone: ${formData.phone || 'Not provided'}\n\n`;

  if (serviceType && serviceMapping?.[serviceType]) {
    message += `🔮 *Service Interest*\n`;
    message += `• ${serviceMapping[serviceType]}\n\n`;
  }

  if (formData.message) {
    message += `💬 *Message*\n`;
    message += `${formData.message}\n\n`;
  }

  message += `---\n`;
  message += `📍 Sent from: ${pageName}`;

  return message;
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  return config.validation.emailRegex.test(email);
}

/**
 * Validate required form fields
 */
export function validateRequired(data: Record<string, string>, requiredFields: string[]): {
  isValid: boolean;
  missingFields: string[];
} {
  const missingFields = requiredFields.filter((field) => !data[field]?.trim());
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}

/**
 * Get the WhatsApp URL for form submission
 */
export function getWhatsAppUrl(formData: Record<string, string>, options: {
  pageName: string;
  serviceType?: string;
  serviceMapping?: Record<string, string>;
}): string {
  const message = formatWhatsAppMessage(formData, options);
  const encodedMessage = encodeURIComponent(message);
  const phoneNumber = config.phoneNumber;
  return `${config.whatsappBaseUrl}?phone=${phoneNumber}&text=${encodedMessage}`;
}

/**
 * Submit form and redirect to WhatsApp
 */
export async function submitFormToWhatsApp(
  form: HTMLFormElement,
  options: FormOptions
): Promise<void> {
  const formData = new FormData(form);
  const data = Object.fromEntries(formData.entries()) as Record<string, string>;

  // Validate required fields
  const { isValid, missingFields } = validateRequired(data, ['name', 'email', 'phone']);
  if (!isValid) {
    throw new Error(`Please fill in required fields: ${missingFields.join(', ')}`);
  }

  if (!validateEmail(data.email)) {
    throw new Error('Please enter a valid email address');
  }

  // Get page name from options or fallback
  const pageName = options.pageName || config.pages.home;
  const serviceMapping = options.serviceMapping || config.services;

  // Build WhatsApp URL
  const whatsappUrl = getWhatsAppUrl(data, {
    pageName,
    serviceType: options.serviceType,
    serviceMapping
  });

  // Open WhatsApp
  window.open(whatsappUrl, '_blank');
}

/**
 * Show a notification to the user
 */
export function showNotification(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
  // Remove existing notifications
  document.querySelectorAll('.notification').forEach((n) => n.remove());

  const notification = document.createElement('div');
  notification.className = `notification ${type}`;
  notification.innerHTML = `
    <div style="display: flex; align-items: center; gap: 10px;">
      <i class="fas ${type === 'success' ? 'fa-check-circle' : type === 'error' ? 'fa-exclamation-circle' : 'fa-info-circle'}"></i>
      <span>${message}</span>
    </div>
  `;

  // Add styles
  Object.assign(notification.style, {
    position: 'fixed',
    top: '20px',
    right: '20px',
    padding: '15px 20px',
    borderRadius: '8px',
    color: 'white',
    fontWeight: '500',
    zIndex: '10000',
    opacity: '0',
    transform: 'translateY(-20px)',
    transition: 'all 0.3s ease',
    maxWidth: '400px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
    backgroundColor: type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6'
  });

  document.body.appendChild(notification);

  // Animate in
  requestAnimationFrame(() => {
    notification.style.opacity = '1';
    notification.style.transform = 'translateY(0)';
  });

  // Auto remove
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transform = 'translateY(-20px)';
    setTimeout(() => notification.remove(), 300);
  }, 5000);
}

/**
 * Initialize form with WhatsApp redirect
 */
export function initFormHandler(options: FormOptions): void {
  const form = document.querySelector(options.formSelector);
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;

    try {
      submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Connecting...';
      submitBtn.disabled = true;

      await submitFormToWhatsApp(form as HTMLFormElement, options);

      // Reset form
      (form as HTMLFormElement).reset();
      showNotification('Opening WhatsApp... Please send the message!', 'success');
    } catch (error) {
      showNotification((error as Error).message, 'error');
    } finally {
      submitBtn.innerHTML = originalText;
      submitBtn.disabled = false;
    }
  });
}
